/**
 * Currency helpers — detect the user's local currency from browser locale
 * and format an FX preview alongside the CAD price. Stripe still charges CAD;
 * this is purely a "know what you're spending in your money" UX affordance.
 */
import axios from "axios";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

// Locale-country → ISO-4217 currency code. Enough to cover the top 30 markets
// without shipping a full CLDR table. Anything else falls back to USD, which
// almost every DJ understands.
const COUNTRY_CURRENCY = {
  US: "USD", CA: "CAD",
  GB: "GBP", IE: "EUR", FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR",
  NL: "EUR", BE: "EUR", PT: "EUR", GR: "EUR", FI: "EUR", AT: "EUR",
  LU: "EUR", MT: "EUR", CY: "EUR", EE: "EUR", LV: "EUR", LT: "EUR",
  SK: "EUR", SI: "EUR", HR: "EUR",
  CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", PL: "PLN", CZ: "CZK", HU: "HUF",
  AU: "AUD", NZ: "NZD", JP: "JPY", CN: "CNY", HK: "HKD", TW: "TWD", KR: "KRW",
  IN: "INR", SG: "SGD", MY: "MYR", ID: "IDR", TH: "THB", PH: "PHP", VN: "VND",
  ZA: "ZAR", NG: "NGN", KE: "KES", GH: "GHS", EG: "EGP", MA: "MAD", TZ: "TZS",
  UG: "UGX", RW: "RWF",
  BR: "BRL", MX: "MXN", AR: "ARS", CL: "CLP", CO: "COP", PE: "PEN",
  AE: "AED", SA: "SAR", QA: "QAR", KW: "KWD", IL: "ILS", TR: "TRY",
  RU: "RUB", UA: "UAH", RO: "RON", BG: "BGN",
};

/** Best-effort detect the user's local currency from browser locale. */
export function detectUserCurrency() {
  try {
    // Try Intl API's resolved currency first (works when the browser has one)
    const opts = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).resolvedOptions();
    // opts.currency is what we PASSED in, so that's useless. We need locale → country.
    const locale = opts.locale || navigator.language || "en-US";
    // "en-US", "en_US", "fr-CA"; try the second segment first, then heuristics.
    const parts = locale.replace("_", "-").split("-");
    const region = (parts[1] || parts[0] || "").toUpperCase();
    if (COUNTRY_CURRENCY[region]) return COUNTRY_CURRENCY[region];
    // Timezone-based fallback (works when locale is bare like "en")
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (tz.includes("Lagos") || tz.includes("Africa/Lagos")) return "NGN";
      if (tz.includes("Nairobi")) return "KES";
      if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "INR";
      if (tz.includes("London")) return "GBP";
      if (tz.includes("Tokyo")) return "JPY";
      if (tz.includes("Sydney") || tz.includes("Melbourne")) return "AUD";
      if (tz.startsWith("America/")) {
        if (tz.includes("Toronto") || tz.includes("Vancouver") || tz.includes("Montreal") || tz.includes("Winnipeg") || tz.includes("Halifax") || tz.includes("Edmonton")) return "CAD";
        return "USD";
      }
      if (tz.startsWith("Europe/")) return "EUR";
    } catch { /* ignore */ }
  } catch { /* ignore */ }
  return "USD";
}

/** Symbol for a currency code, best-effort. */
function symbolFor(code) {
  try {
    // Format 0 in that currency and strip the digits — leaves the symbol
    const s = new Intl.NumberFormat("en-US", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(0);
    return s.replace(/[\d\s.,]/g, "");
  } catch {
    return code + " ";
  }
}

// Currency → primary-country flag emoji. Covers ~50 currencies; anything
// unknown falls back to 🏳️ (white flag) which reads as "neutral / unknown".
const CURRENCY_FLAG = {
  USD:"🇺🇸", CAD:"🇨🇦", GBP:"🇬🇧", EUR:"🇪🇺", AUD:"🇦🇺", NZD:"🇳🇿", JPY:"🇯🇵",
  CNY:"🇨🇳", HKD:"🇭🇰", TWD:"🇹🇼", KRW:"🇰🇷", INR:"🇮🇳", SGD:"🇸🇬", MYR:"🇲🇾",
  IDR:"🇮🇩", THB:"🇹🇭", PHP:"🇵🇭", VND:"🇻🇳", CHF:"🇨🇭", SEK:"🇸🇪", NOK:"🇳🇴",
  DKK:"🇩🇰", PLN:"🇵🇱", CZK:"🇨🇿", HUF:"🇭🇺", RON:"🇷🇴", BGN:"🇧🇬", RUB:"🇷🇺",
  UAH:"🇺🇦", TRY:"🇹🇷", ILS:"🇮🇱", AED:"🇦🇪", SAR:"🇸🇦", QAR:"🇶🇦", KWD:"🇰🇼",
  EGP:"🇪🇬", MAD:"🇲🇦", ZAR:"🇿🇦", NGN:"🇳🇬", KES:"🇰🇪", GHS:"🇬🇭", TZS:"🇹🇿",
  UGX:"🇺🇬", RWF:"🇷🇼", BRL:"🇧🇷", MXN:"🇲🇽", ARS:"🇦🇷", CLP:"🇨🇱", COP:"🇨🇴",
  PEN:"🇵🇪",
};

/** Best-effort flag emoji for a currency code. */
export function flagFor(currency) {
  return CURRENCY_FLAG[currency] || "🏳️";
}

let _cachedRates = null;
let _cacheAt = 0;

/** Fetch the CAD→others rate table. Client-side cached 30 min so we don't
 *  hit the backend every render. */
export async function loadFxRates() {
  const now = Date.now();
  if (_cachedRates && (now - _cacheAt) < 30 * 60 * 1000) return _cachedRates;
  try {
    const r = await axios.get(`${API}/fx/rates`, { timeout: 6000 });
    _cachedRates = r.data;
    _cacheAt = now;
    return _cachedRates;
  } catch {
    return null;
  }
}

/**
 * Given a CAD amount and the FX rate blob, return the localized-preview
 * string like "≈ US$3.65" or null if we can't compute one (or the user is
 * already in CAD — no point showing "≈ CA$4.99").
 */
export function formatLocalPreview(cadAmount, rates, targetCurrency) {
  if (!rates || !targetCurrency || targetCurrency === "CAD") return null;
  const rate = rates.rates?.[targetCurrency];
  if (!rate || !isFinite(rate)) return null;
  const converted = cadAmount * rate;
  // Round smartly — currencies like JPY / KRW don't use decimals; others use 2.
  const zeroDecimal = ["JPY", "KRW", "VND", "IDR", "HUF", "CLP", "UGX", "RWF", "TZS", "TWD"].includes(targetCurrency);
  const formatted = zeroDecimal
    ? Math.round(converted).toLocaleString()
    : converted.toFixed(2);
  return `${symbolFor(targetCurrency)}${formatted} ${targetCurrency}`;
}

/**
 * Same as `formatLocalPreview` but the source amount is in USD (tips + merch
 * flow in USD via Stripe Connect). Converts USD → CAD → target using the
 * CAD-based rate table.
 */
export function formatLocalPreviewFromUsd(usdAmount, rates, targetCurrency) {
  if (!rates || !targetCurrency || targetCurrency === "USD") return null;
  const usdRate = rates.rates?.USD;
  const targetRate = rates.rates?.[targetCurrency];
  if (!usdRate || !targetRate || !isFinite(usdRate) || !isFinite(targetRate)) return null;
  // usd -> cad = usd / cad_to_usd_rate ; cad -> target = cad * cad_to_target_rate
  const cadEquivalent = usdAmount / usdRate;
  const converted = cadEquivalent * targetRate;
  const zeroDecimal = ["JPY", "KRW", "VND", "IDR", "HUF", "CLP", "UGX", "RWF", "TZS", "TWD"].includes(targetCurrency);
  const formatted = zeroDecimal
    ? Math.round(converted).toLocaleString()
    : converted.toFixed(2);
  return `${symbolFor(targetCurrency)}${formatted} ${targetCurrency}`;
}
